/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  APIConsumerResponseInterface,
  ChargerHistoryProperties,
  EndConsumerActivityLogs,
  EndConsumerInitialProperties,
  EndConsumerInterFace,
  PersonalChargerProperties,
  RequestEndConsumerInterFace,
} from '../../interface/end-consumer.interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { arrayGroupBy, removeEmptyKeys } from '../../shared-functions/DateTime';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import endConsumerApi from '../../utils/api/end-consumer-v2';
import endConsumerV2Api from '../../utils/api/v2/end-consumer';

import { setNotificationMessage } from './NotificationManagementSlice';

// a thunk to fetch users
export const fetchAllEndConsumers = createAsyncThunk<
  APIConsumerResponseInterface,
  RequestEndConsumerInterFace
>(
  'end-consumer-management/fetchAllEndConsumers',
  async (data: RequestEndConsumerInterFace, { dispatch }) => {
    const response = await endConsumerV2Api
      .fetchConsumers(removeEmptyKeys(data))
      .then((response1) => response1)
      .catch((error) =>
        dispatch(
          setNotificationMessage({
            type: 'error',
            message: error?.response?.data?.message,
            code: error?.response?.data?.code,
            status: true,
          }),
        ),
      );
    return {
      ...response.data,
      results: response.data.results.map((consumerData: EndConsumerInterFace) => ({
        ...consumerData,
        createdAt: consumerData.createdAt,
        name: `${consumerData.firstName} ${consumerData.lastName}`,
        id: consumerData._id,
        wallet: Number(consumerData?.wallet).toFixed(2),
      })),
    };
  },
);

// a thunk to refresh end consumers
export const refreshAllEndConsumers = createAsyncThunk<
  APIConsumerResponseInterface,
  RequestEndConsumerInterFace
>(
  'end-consumer-management/refreshAllEndConsumers',
  async (data: RequestEndConsumerInterFace) => {
    const response = await endConsumerV2Api.fetchConsumers(removeEmptyKeys(data));
    return {
      ...response.data,
      results: response.data.results.map((consumerData: EndConsumerInterFace) => ({
        ...consumerData,
        createdAt: consumerData.createdAt,
        name: `${consumerData.firstName} ${consumerData.lastName}`,
        id: consumerData._id,
        wallet: Number(consumerData?.wallet).toFixed(2),
      })),
    };
  },
);


// Fetch session user
export const fetchEndConsumerActivityLogs = createAsyncThunk<EndConsumerActivityLogs[], string>(
  'end-consumer-management/fetchEndConsumerActivityLogs',
  async (id: string, { dispatch }) => {
    try {
      const response = await endConsumerApi.fetchActivityLogs(id);
      let rawData: any[] = [];
      if (Array.isArray(response?.data)) {
        rawData = response.data;
      } else if (Array.isArray(response?.data?.data)) {
        rawData = response.data.data;
      } else if (Array.isArray(response?.data?.results)) {
        rawData = response.data.results;
      }
      return rawData.map((consumerData: any) => ({
        ...consumerData,
        date: ConvertUTCtoDate(consumerData.createdAt),
        time: FormatUtcString(ConvertUTCtoHHMM(consumerData.createdAt)),
        id: consumerData._id,
      }));
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// a thunk to fetch devices filters
export const fetchListEndConsumerFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  void // No need for an argument, since we are not using the payload
>('end-consumer-management/fetchListEndConsumerFilters', async (_, { dispatch }) => {
  try {
    const response = await endConsumerApi.fetchConsumersFilterList();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

export const fetchPersonalChargers = createAsyncThunk<
  PersonalChargerProperties[],
  PersonalChargerProperties
>(
  'end-consumer-management/fetchPersonalChargers',
  async (query: PersonalChargerProperties, { dispatch }) => {
    try {
      const response = await endConsumerV2Api.personalChargerDetails(query?.id, query);
      return response?.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

export const fetchPersonalChargersHistory = createAsyncThunk<
  ChargerHistoryProperties[],
  string | undefined
>('end-consumer-management/fetchPersonalChargersHistory', async (id, { dispatch }) => {
  try {
    const response = await endConsumerApi.personalChargerHistory(id);
    return response.data;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

// Initial state
const initialState: EndConsumerInitialProperties = {
  consumers: [],
  activityLogs: [],
  personalChargers: [],
  chargerHistory: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filter: {},
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
  sidePanelSortData: '',
  sidePanelSortField: '',
  endConsumerTotalResults: 0,
  endConsumerTotalPages: 0,
  endConsumerLimit: DefaultApiParameters.limit,
  endConsumerPage: DefaultApiParameters.page,
};

// Create the slice
export const endConsumerSlice = createSlice({
  name: 'end-consumer-management',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updateEndConsumerFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state - The current state.
     */
    disableEndConsumeFiltersApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
    /**
     * function for set sortBy
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setSidepanelSortData: (
      state,
      action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>,
    ) => {
      state.sidePanelSortField = action.payload.sortField;
      state.sidePanelSortData = action.payload.sortBy;
    },
    /**
     * function for set page and limit
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setEndConsumerPage: (state, action: PayloadAction<any>) => {
      state.endConsumerPage = action.payload?.page;
      state.endConsumerLimit = action.payload?.limit;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder: ActionReducerMapBuilder<EndConsumerInitialProperties>) => {
    builder
      .addCase(fetchAllEndConsumers.pending, (state: EndConsumerInitialProperties) => {
        state.loading = true;
        state.consumers = []; // Clear old tenant data immediately
      })
      .addCase(
        fetchAllEndConsumers.fulfilled,
        (
          state: EndConsumerInitialProperties,
          action: PayloadAction<APIConsumerResponseInterface>,
        ) => {
          state.loading = false;
          state.consumers = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchAllEndConsumers.rejected, (state: EndConsumerInitialProperties) => {
        state.loading = false;
        state.consumers = []; // Clear on rejection
        // Handle error if needed
      });
    builder.addCase(refreshAllEndConsumers.fulfilled, (state, action) => {
      state.consumers = [...action.payload.results];
      state.limit = action.payload.limit;
      state.page = action.payload.page;
      state.totalPages = action.payload.totalPages;
      state.totalResults = action.payload.totalResults;
    });
    builder
      .addCase(fetchEndConsumerActivityLogs.pending, (state) => {
        state.loading = false;
      })
      .addCase(
        fetchEndConsumerActivityLogs.fulfilled,
        (state, action: PayloadAction<EndConsumerActivityLogs[]>) => {
          state.loading = false;
          state.activityLogs = Object.entries(arrayGroupBy(action.payload, 'date')).map(
            (value) => ({
              logs: value[1].reverse(),
              date: value[0],
            }),
          );
        },
      )
      .addCase(fetchEndConsumerActivityLogs.rejected, (state) => {
        state.loading = false;
      });
    builder
      .addCase(fetchListEndConsumerFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListEndConsumerFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchListEndConsumerFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
    builder
      .addCase(fetchPersonalChargers.pending, (state) => {
        state.loading = false;
      })
      .addCase(fetchPersonalChargers.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.personalChargers = action.payload.results;
        state.endConsumerTotalResults = action.payload.totalResults;
        state.endConsumerTotalPages = action.payload.totalPages;
        state.endConsumerLimit = action.payload.limit;
        state.endConsumerPage = action.payload.page;
      })
      .addCase(fetchPersonalChargers.rejected, (state) => {
        state.loading = false;
      });
    builder
      .addCase(fetchPersonalChargersHistory.pending, (state) => {
        state.loading = false;
      })
      .addCase(
        fetchPersonalChargersHistory.fulfilled,
        (state, action: PayloadAction<ChargerHistoryProperties[]>) => {
          state.loading = false;
          state.chargerHistory = action.payload;
        },
      )
      .addCase(fetchPersonalChargersHistory.rejected, (state) => {
        state.loading = false;
      });
  },
});

// Export the actions
export const {
  updateEndConsumerFilters,
  disableEndConsumeFiltersApplied,
  setSortData,
  setSidepanelSortData,
  setEndConsumerPage,
} = endConsumerSlice.actions;
// Export the reducer
export default endConsumerSlice.reducer;
